import { useMemo } from "react";

import { useApiKeys, useReports } from "../app/queries";
import { relativeTime } from "../lib/format";
import type { ApiKeySummary, ReportSummary } from "../lib/api";

export interface MetricsSummary {
  total: number;
  openCritical: number;
  recentCount: number;
  activeKeys: number;
  totalKeys: number;
  lastReceivedRelative: string;
  lastReceivedExact: string;
  lastKeyUsedAt: string | null;
}

/**
 * useProjectMetrics aggregates the dashboard metrics (open critical,
 * recent count, last activity, key usage) from the cached report and
 * key lists. It does not issue new requests — it derives everything
 * from the queries that the page (or its parent) has already
 * triggered, so it's safe to call from any child component.
 *
 * The hook also surfaces the underlying report list (and a 5-item
 * "recent" slice) so callers don't need to call useReports separately.
 */
export function useProjectMetrics(projectId: string) {
  const reports = useReports(projectId);
  const keys = useApiKeys(projectId);

  const items = useMemo(
    () => reports.data?.reports ?? [],
    [reports.data],
  );
  const metrics = useMemo<MetricsSummary>(
    () => computeMetrics(items, keys.data?.keys ?? []),
    [items, keys.data],
  );
  const recent = useMemo(() => items.slice(0, 5), [items]);

  return { metrics, reports, recent };
}

export function computeMetrics(
  reports: ReportSummary[],
  keys: Pick<ApiKeySummary, "revokedAt" | "lastUsedAt">[],
): MetricsSummary {
  const now = Date.now();
  const cutoff = now - 24 * 60 * 60 * 1000;

  let openCritical = 0;
  let recentCount = 0;
  let lastReceivedAt: number | null = null;
  let lastReceivedIso: string | null = null;

  for (const r of reports) {
    const isOpen = r.status !== "resolved" && r.status !== "ignored";
    if (isOpen && r.severity === "critical") openCritical++;
    const ts = new Date(r.receivedAt).getTime();
    if (!isNaN(ts)) {
      if (ts >= cutoff) recentCount++;
      if (lastReceivedAt === null || ts > lastReceivedAt) {
        lastReceivedAt = ts;
        lastReceivedIso = r.receivedAt;
      }
    }
  }

  const totalKeys = keys.length;
  let activeKeys = 0;
  let lastKeyUsedAt: string | null = null;
  let lastKeyUsedTs = -1;
  for (const k of keys) {
    if (!k.revokedAt) activeKeys++;
    if (k.lastUsedAt) {
      const ts = new Date(k.lastUsedAt).getTime();
      if (!isNaN(ts) && ts > lastKeyUsedTs) {
        lastKeyUsedTs = ts;
        lastKeyUsedAt = k.lastUsedAt;
      }
    }
  }

  return {
    total: reports.length,
    openCritical,
    recentCount,
    activeKeys,
    totalKeys,
    lastReceivedRelative: lastReceivedIso ? relativeTime(lastReceivedIso) : "",
    lastReceivedExact: lastReceivedIso ?? "",
    lastKeyUsedAt,
  };
}
